import { createRouter, createWebHistory } from 'vue-router'
import routes from './routes' // Assuming all route definitions are moved here
import store from '@/store'
import cognitoAuth from '@/cognito'
import { authHelpers } from '@/api/authHelpers'
import { isPaywallEnabled } from '@/utils/featureFlags'
import client from '@/graphql/client'
import GetProject from '@/graphql/queries/GetProject'
import { hasUnusableRouteParam } from './routeGuards'

const allowedRoutesListWithoutSubscription = ['/plans', '/checkout', '/payment', '/account']
const billingRoutePrefixes = ['/plans', '/checkout', '/payment', '/subscription']

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to, from, next) => {
  // A cleared workflow id can produce a malformed URL like /workflow/undefined/undefined
  // (a router.push built from undefined params). Such a route renders a dead "Not found"
  // page, and if the user is logged out there it gets preserved as the post-login redirect —
  // trapping them on it. Bounce any route carrying a literal "undefined"/"null" param to the
  // dashboard before any auth/redirect handling runs.
  if (hasUnusableRouteParam(to.params)) {
    next('/dashboard')
    return
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresNoAuth = to.matched.some(record => record.meta.requiresNoAuth)
  const requiresProject = to.matched.some(record => record.meta.requiresProject)
  const allowNonTeamMember = to.matched.some(record => record.meta.allowNonTeamMember)

  try {
    const authenticated = await cognitoAuth.isAuthenticatedPromise()

    // If Cognito has a valid session but the store flags are stale (e.g. localStorage
    // was cleared or the user logged in before adminUser/isSuperAdmin were persisted),
    // re-seed the store from the live ID token so direct navigation to protected routes
    // (like /admin) works without requiring a fresh login.
    if (authenticated && !store.state.isUserLoggedIn) {
      const idTokenPayload = authenticated.getIdToken().decodePayload()
      let adminUser = false
      let isSuperAdmin = false
      if (idTokenPayload['cognito:groups']) {
        if (idTokenPayload['cognito:groups'][0] === 'Admin') {
          adminUser = true
        }
        if (idTokenPayload['cognito:groups'].includes('SuperAdmin')) {
          isSuperAdmin = true
        }
      }
      store.commit('login', {
        email: idTokenPayload.email,
        adminUser,
        isSuperAdmin,
        userId: idTokenPayload['cognito:username']
      })
    }

    if (requiresAuth && (!authenticated || !store.state.isUserLoggedIn)) {
      handleUnauthenticatedUser(to, next)
      return
    }

    if (authenticated) {
      await handleAuthenticatedUser(to, from, next, requiresNoAuth, requiresProject, allowNonTeamMember)
      return
    }

    if (shouldRedirectToWorkflow(to, from)) {
      redirectToWorkflow(to, from, next)
      return
    }

    next()
  } catch (error) {
    console.error('Authentication error', error)
    authHelpers.logoutAndClearStore()
    next('/login/')
  }
})

function handleUnauthenticatedUser (to, next) {
  authHelpers.logoutAndClearStore()
  // Preserve the full intended path so login returns the user to exactly where they
  // were headed (e.g. a workflow), not just the project dashboard. The login page only
  // honours an internal `redirect` (it checks startsWith('/')), so this is open-redirect safe.
  const params = new URLSearchParams()
  if (to.fullPath && to.fullPath.startsWith('/') && to.fullPath !== '/') {
    params.set('redirect', to.fullPath)
  }
  if (to.params.projectId) {
    params.set('projectId', to.params.projectId)
  }
  const qs = params.toString()
  next(`/login${qs ? `?${qs}` : '/'}`)
}

async function handleAuthenticatedUser (to, from, next, requiresNoAuth, requiresProject, allowNonTeamMember) {
  if (allowNonTeamMember) {
    nextWithQueryParams(to, from, next)
    return
  }

  if (requiresNoAuth) {
    next()
    return
  } else {
    if (!isPaywallEnabled() && billingRoutePrefixes.some((prefix) => to.fullPath.startsWith(prefix))) {
      next('/dashboard')
      return
    }
    if (!haveActiveSubscription(to.fullPath)) {
      next('/plans')
      return
    }
  }

  const validProject = requiresProject ? await projectIsValid(to.params.projectId) : true

  if (!validProject) {
    next('/404')
    return
  }

  // A route carrying an org makes it the current org (URL overrides persisted).
  if (to.params.orgId) {
    store.dispatch('currentOrg/resolveFromRoute', to.params.orgId)
  }

  const requiresOrgOwner = to.matched.some(record => record.meta.requiresOrgOwner)
  if (requiresOrgOwner) {
    const orgId = to.params.orgId
    const isSuperAdmin = store.state.isSuperAdmin
    if (!isSuperAdmin) {
      if (!store.state.orgMembers.membersByOrg[orgId]) {
        await store.dispatch('orgMembers/listOrgMembers', { orgId })
      }
      const isOwner = store.getters['orgMembers/isOrgOwner'](orgId)
      if (!isOwner) {
        next('/404')
        return
      }
    }
  }
  nextWithQueryParams(to, from, next)
}

function nextWithQueryParams (to, from, next) {
  if (!hasQueryParams(to) && hasQueryParams(from) && !!from.query.selectedTab && to.path.includes('/workflow/')) {
    // keep selected tab open
    next({
      path: to.path,
      query: from.query
    })
  } else {
    next()
  }
}

function redirectToWorkflow (to, from, next) {
  next({
    path: to.path,
    params: to.params,
    query: from.query
  })
}

function shouldRedirectToWorkflow (to, from) {
  return !hasQueryParams(to) && hasQueryParams(from) && from.query.selectedTab && to.path.includes('/workflow/')
}

function hasQueryParams (route) {
  return !!Object.keys(route.query).length
}

// Validate project access for requiresProject routes via the BACKEND (single source of
// truth): getProject returns the project iff the caller can access it (org member,
// SuperAdmin, or legacy TeamMember; null otherwise). This replaced a TeamMember-only
// client check (checkProjectInMemberships) that 404'd org-RBAC users — who have no
// TeamMember row — on the project dashboard. SuperAdmin short-circuits to avoid a call.
// ROLLBACK: the old client-side TeamMember check is in git history (see the commit that
// introduced GetProject in the router); revert to restore checkProjectInMemberships.
async function projectIsValid (projectId) {
  if (!projectId) {
    return false
  }
  if (store.state.isSuperAdmin) {
    return true
  }
  try {
    const { getProject } = await client.query({
      operationName: 'getProject',
      query: GetProject,
      variables: { projectId }
    })

    return !!getProject
  } catch (e) {
    console.warn('projectIsValid: getProject check failed', e)

    return false
  }
}

function haveActiveSubscription (fullPath) {
  const subscriptionAccessType = store.state.payments.subscriptionAccessType
  const activeSubscription = store.state.payments.activeSubscription
  const loadingActiveSubscription = store.state.payments.loadingActiveSubscription
  const shouldAccessAdmin = store.state.adminUser && fullPath?.startsWith('/admin')

  return !(!loadingActiveSubscription &&
    !!activeSubscription &&
    !allowedRoutesListWithoutSubscription.includes(fullPath) &&
    subscriptionAccessType === 'NO_ACCESS' &&
    !shouldAccessAdmin)
}

export default router
